import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon.js';

/** The "⋯" actions button on a card, with its pop-over menu. */
export function CardMenu({
  label,
  children,
  width,
}: {
  label: string;
  children: (close: () => void) => ReactNode;
  /** Menu width (descriptions need room); default fits the labels. */
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="icon-btn"
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <Icon name="dot" size={4} />
        <Icon name="dot" size={4} style={{ margin: '0 2px' }} />
        <Icon name="dot" size={4} />
      </button>
      {open && (
        <div
          role="menu"
          className="card"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 20,
            padding: 5,
            minWidth: 150,
            width,
            boxShadow: 'var(--sh-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

export function MenuItem({
  icon,
  label,
  description,
  onClick,
  danger,
  disabled,
}: {
  icon: string;
  label: string;
  /** One line under the label saying what it does (or why it's off). */
  description?: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: description ? 'flex-start' : 'center',
        gap: 8,
        padding: '7px 10px',
        border: 'none',
        background: 'transparent',
        borderRadius: 6,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        fontSize: 13,
        fontWeight: description ? 700 : 500,
        textAlign: 'left',
        whiteSpace: description ? 'normal' : 'nowrap',
        color: danger ? 'var(--st-declined)' : 'var(--text-1)',
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = 'var(--bg-sunken)';
      }}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <Icon name={icon} size={15} style={description ? { marginTop: 2, flex: 'none' } : { flex: 'none' }} />
      {description ? (
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{label}</span>
          <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-3)' }}>{description}</span>
        </span>
      ) : (
        label
      )}
    </button>
  );
}
